import React, { useEffect, useCallback } from 'react';
import { createSelector } from 'reselect';
import noop from 'lodash/noop';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { useDispatch, useSelector } from '@atlassian/jira-react-redux/src/index.tsx';

import {
	commentRepliesSelector,
	childCommentsPageInfoSelector,
	loadingStageSelector,
	isReplyingToCommentSelector,
} from '@atlassian/jira-issue-view-store/src/selectors/comment-selector.tsx';
import { COMMENTS } from '@atlassian/jira-issue-view-common-constants/src/activity-items.tsx';
import type { State } from '@atlassian/jira-issue-view-common-types/src/issue-type.tsx';
import { getPermalinkStatus } from '@atlassian/jira-platform-issue-permalinks/src/index.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	fetchNewerChildCommentsRequest,
	fetchOlderChildCommentsRequest,
} from '@atlassian/jira-issue-view-store/src/actions/comment-actions.tsx';
import { usePrevious } from '@atlassian/jira-platform-react-hooks-use-previous/src/common/utils/index.tsx';
import {
	ActionButton,
	SAVE_CHILD_COMMENT_LOADING_PREFIX,
} from '@atlassian/jira-issue-view-common-types/src/comment-type.tsx';
import Comment from '../../comment-view/index.tsx';
import ReplyCommentWrapper from '../../comment-reply-wrapper/index.tsx';
import ShowMoreRepliesButton from '../../comment-show-more-replies/index.tsx';

export type CommentRepliesComponentProps = {
	commentSessionId?: string | null;
	fullIssueUrl: string | null;
	commentId: string;
};

const memoizedReplies = createSelector(
	(commentId: string, state: State) => commentRepliesSelector(commentId)(state),
	(result) => result,
);

const memoizedPagesInfo = createSelector(
	(commentId: string, state: State) => childCommentsPageInfoSelector(commentId)(state),
	(result) => result,
);
const memoizedReplyEditor = createSelector(
	(commentId: string, state: State) => isReplyingToCommentSelector(commentId)(state),
	(result) => result,
);
export const CommentReplies = (props: CommentRepliesComponentProps) => {
	const { fullIssueUrl, commentSessionId, commentId } = props;
	const dispatch = useDispatch();
	const isReplyingToComment = useSelector((state: State) => memoizedReplyEditor(commentId, state));
	const replies = useSelector((state: State) => memoizedReplies(commentId, state));
	const childCommentsPageInfo = useSelector((state: State) => memoizedPagesInfo(commentId, state));
	const loadingStage = useSelector((state: State) => loadingStageSelector(state));

	const getMoreOlderComments = useCallback(() => {
		dispatch(fetchOlderChildCommentsRequest(commentId));
	}, [dispatch, commentId]);
	const getMoreNewerComments = useCallback(() => {
		dispatch(fetchNewerChildCommentsRequest(commentId));
	}, [dispatch, commentId]);

	const previousRepliesLength = usePrevious(replies?.length);

	useEffect(() => {
		if (childCommentsPageInfo) {
			const repliesLength = replies?.length;

			if (previousRepliesLength !== repliesLength && (!replies || repliesLength === 0)) {
				if (childCommentsPageInfo.hasNextPage) {
					getMoreOlderComments();
				} else if (childCommentsPageInfo.hasPreviousPage) {
					getMoreNewerComments();
				}
			}
		}
	}, [
		replies,
		childCommentsPageInfo,
		previousRepliesLength,
		getMoreNewerComments,
		getMoreOlderComments,
	]);

	if (!replies || replies.length === 0) return null;

	const { hasPermalink, permalinkId } = getPermalinkStatus(COMMENTS);

	const showOlderComments = (analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'showMoreReplies', {
			olderComments: true,
		});
		getMoreOlderComments();
	};

	const showNewerComments = (analyticsEvent: UIAnalyticsEvent) => {
		fireUIAnalytics(analyticsEvent, 'showMoreReplies', {
			newerComments: true,
		});
		getMoreNewerComments();
	};

	const hasReplies = replies && replies.length > 0;

	const isNewChildCommentSaving =
		loadingStage === `${SAVE_CHILD_COMMENT_LOADING_PREFIX}${commentId}`;

	const isLatestChildCommentNotVisible = childCommentsPageInfo?.hasPreviousPage;

	if (isNewChildCommentSaving && isLatestChildCommentNotVisible) {
		return (
			<ShowMoreRepliesButton
				onClickHandler={noop}
				commentId={commentId}
				type={ActionButton.PREVIOUS}
				forceShowLoadingState
			/>
		);
	}

	return (
		<>
			{childCommentsPageInfo?.hasNextPage && (
				<ShowMoreRepliesButton
					onClickHandler={showOlderComments}
					commentId={commentId}
					type={ActionButton.PREVIOUS}
				/>
			)}
			{hasReplies &&
				replies.map((childCommentId, index) => (
					<ReplyCommentWrapper
						key={childCommentId}
						isFirst={index === 0 && !childCommentsPageInfo?.hasNextPage}
						isLast={
							index === replies.length - 1 &&
							!isReplyingToComment &&
							!childCommentsPageInfo?.hasPreviousPage
						}
					>
						<Comment
							commentId={childCommentId}
							fullIssueUrl={fullIssueUrl}
							isHighlighted={hasPermalink && childCommentId === permalinkId}
							commentSessionId={commentSessionId}
							isThreadedCommentsEnabled
						/>
					</ReplyCommentWrapper>
				))}
			{childCommentsPageInfo?.hasPreviousPage && (
				<ShowMoreRepliesButton
					onClickHandler={showNewerComments}
					commentId={commentId}
					type={ActionButton.NEXT}
					isReplyingToComment={isReplyingToComment}
				/>
			)}
		</>
	);
};

export default CommentReplies;
